import { types, flow, getEnv, getSnapshot, type Instance } from 'mobx-state-tree';
import Project, { type IProject } from './project';
import type { IModelVersion } from './model_version';
import type { IStoreEnv } from '../env';
import Model from '../model';

/**
 * Hand-declared instance interface for the Projects (ProjectList) store. The
 * actions block is declared *before* the views block in this store, so the
 * actions reference views that don't yet exist on the raw `self`
 * (`getProjectById`); per the MST typing pattern (§4) we rebind `self` to this
 * interface in the actions block so those forward view references type.
 */
export interface IProjects {
  readonly projects: IProject[] | null;
  readonly activeProject: IProject | null;
  readonly activeModelVersion: IModelVersion | null | undefined;
  readonly initialized: boolean;
  readonly count: number;
  readonly recentProjects: IProject[];
  readonly myProjects: IProject[] | null;
  getProjectById(id: string): IProject | undefined;
  initialize(): Promise<void>;
  createProject(
    name: string,
    settings: Record<string, unknown>,
    content?: unknown
  ): Promise<string>;
  setActiveProject(id: string | null): void;
  removeProject(id: string): Promise<void>;
}

/**
 * A list of all projects and a reference to the currently active project.
 */
const Projects = types
  .model('ProjectList', {
    // the list of all projects for the currently logged in user
    projects: types.maybeNull(types.array(Project)),
    // the active project (i.e. the project currently open)
    activeProject: types.maybeNull(types.reference(Project)),
  })
  .actions(self0 => {
    // Rebind `self` to the hand-declared interface so the actions can reference
    // views (`getProjectById`) defined in the *later* views block (§4). Property
    // writes stay on the raw node `self0`.
    const self = self0 as unknown as IProjects;
    return {
    // fetch all projects from the api and store them in our list
    initialize: flow(function* initialize() {
      const api = getEnv<IStoreEnv>(self0).api;
      const projects = yield api.projects.listProjects();
      self0.projects = projects;
    }),
    /**
     * Create a new project, including a model and a version (aka 'the working
     * version')
     *
     * @param {string} name the name for the project
     * @param {object} settings the settings for the model (notation,
     * propagateOnDependency, etc.)
     * @param {object} content the content for the version created (i.e. the
     * MERODE model)
     */
    createProject: flow(function* createProject(
      name: string,
      settings: Record<string, unknown>,
      content?: unknown
    ) {
      const api = getEnv<IStoreEnv>(self0).api;
      const emptyModel = getSnapshot(Model.create({}));
      // Plain JSON payload sent to the backend; `version` is reassigned below, so
      // type it loosely (the shape is the API contract, not an MST node).
      const project: { name: string; model: { name: string; version: unknown } } = {
        name,
        model: {
          name,
          ...settings,
          version: {
            name: 'working version',
            content: content || emptyModel,
          },
        },
      };
      if (content) {
        project.model.version = {
          name,
          content,
        };
      }
      const result = yield api.projects.createProject(project);
      // Legacy behaviour: `projects` is non-null once `initialize` has run.
      self0.projects!.push(result);
      return result.id;
    }),
    setActiveProject(id: string | null) {
      // Guard against unknown ids: assigning a types.reference(Project) to an id
      // that does not resolve throws. Fall back to null so the Project component
      // can render the UnknownProject screen instead of crashing the app.
      // MST accepts an identifier string when writing a reference, but the TS
      // write type is the resolved instance, so cast at the assignment.
      self0.activeProject = (id != null && self.getProjectById(id) != null
        ? id
        : null) as unknown as typeof self0.activeProject;
    },
    /**
     * Remove a project.
     *
     * @param {string} id of the project to remove
     */
    removeProject: flow(function* removeProject(id: string) {
      const project = self.getProjectById(id);
      if (project != null) {
        try {
          const api = getEnv<IStoreEnv>(self0).api;
          yield api.projects.removeProject(id);
        } catch (err) {
          // ILB: it will be gone locally, can be retried later on
        }
        // `project` is the resolved instance from the (non-null) projects array.
        self0.projects!.remove(project as unknown as Instance<typeof Project>);
      }
    }),
    };
  })
  .views(self0 => {
    // Rebind `self` to the hand-declared interface so the sibling view
    // `recentProjects`/`getProjectById` and the `activeProject` reference read
    // concretely (§4).
    const self = self0 as unknown as IProjects;
    return {
    // get the active model version for the currently active project
    get activeModelVersion() {
      if (self.activeProject) {
        return self.activeProject.activeModelVersion;
      } else {
        return null;
      }
    },
    // we are initialized if the list of projects is not null
    get initialized() {
      return self.projects !== null;
    },
    // get the number of projects
    get count() {
      return self.projects ? self.projects.length : 0;
    },
    // get the 'recent projects': the projects with a lastActivity timestamp in
    // the last two weeks.
    get recentProjects() {
      // Legacy behaviour: `recentProjects` is only read once `projects` is loaded.
      return self.projects!.filter(project => {
        // last two weeks
        return project.lastActivity.diffNow('days').days > -14;
      });
    },
    get myProjects() {
      // Shared projects not yet supported -> all projects are mine
      return self.projects;
    },
    getProjectById(id: string) {
      // Legacy behaviour: `getProjectById` is only called once `projects` exists.
      return self.projects!.find(p => p.id === id);
    },
    };
  });

export default Projects;
